<template>
  <div class="soar-container">
    <FileValiditySkeleton v-if="isLoading"></FileValiditySkeleton>
    <template v-else>
      <div class="w-full my-6 flex flex-col">
        <div class="border-b border-brand-gray-350 py-3">
          <h2 class="text-xl text-gray-500 font-normal mx-3">
            {{ searchTitle }}
          </h2>
        </div>
        <div class="flex items-center p-6">
          <div class="w-full flex gap-x-4 gap-y-1 text-brand-black-secondary">
            <InfoBar>
              <span class="flex-1 text-justify my-3 text-white text-base h-full w-full">
                {{ $t('sponsoredManageNote') }}
              </span>
            </InfoBar>
          </div>
        </div>
        <div class="w-full">
          <Form
            :initial-values="initialValues"
            :keep-values="true"
            class="flex flex-col gap-y-5"
            ref="formValues"
            @submit="(values) => searchEmployee(values)"
            v-slot="{ values }"
          >
            <fieldset class="border-2 border-primary rounded p-4 flex flex-col gap-y-5 mx-4">
              <legend class="px-4 text-xl">{{ $t('search') }}</legend>

              <VisitingSearchPanel
                v-if="pageType == 'visasRelativesFriendsPage'"
                :values="values"
                :countries="countries"
                :modules="modules"
                :serviceStatus="serviceStatus"
                :pageType="pageType"
              />
              <sponsorSearchPanel
                v-else
                :values="values"
                :countries="countries"
                :modules="modules"
                :serviceStatus="serviceStatus"
                :pageType="pageType"
              />
              <div class="flex justify-end items-center w-full mt-2">
                <FilledButton class="px-5 py-1.5 text-white">
                  {{ $t('search') }}
                </FilledButton>
              </div>
            </fieldset>
          </Form>
        </div>
        <div class="border-b w-full border-brand-gray-300 p-4"></div>
        <div class="my-8">
          <Table
            :tableData="tableData"
            :initialValues="initialValues"
            :pageLength="pageLength"
            :modules="extendModules"
            :parentService="parentService"
            :service="service"
            :countries="countries"
            :serviceStatus="serviceStatus"
            :transaction="transaction"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import InfoBar from '@/components/Form/InfoBar.vue'
import FileValiditySkeleton from '@/views/file-validity/skeleton.vue'
import Table from '@/views/individual/sponsored/sponsorTable.vue'
import VisitingSearchPanel from '@/views/individual/sponsored/VisitingSearchPanel.vue'
import sponsorSearchPanel from '@/views/individual/sponsored/sponsorSearchPanel.vue'
import FilledButton from '@/components/Buttons/Filled.vue'
import { useLookupsStore } from '@/stores/lookups'
import { Form } from 'vee-validate'
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import apiHelperService from '@/services'
import { useFormStore } from '@/stores/form'
import lookups from '@/assets/json/clientLookups.json'
import client_config from '@/assets/json/client_config.json'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

const lookupsStore = useLookupsStore()
const formStore = useFormStore()
const route = useRoute()
const i18n = useI18n()

const formValues = ref()

const isLoading = ref(false)
const modules = ref([])
const parentService = ref([])
const service = ref([])
const transaction = ref([])
const countries = ref([])
const serviceStatus = ref([])
const specSponsoredOrderBy = ref([])
const currentLanguage = ref('en')
const tableData = ref([])
const pageLength = ref(10)
const initialValues = ref({
  currentPage: 1,
  pageSize: 10
})
const pageType = ref()
const searchTitle = ref()

const extendModules = ref([])

const searchEmployee = async (value) => {
  tableData.value = await callServer(value)
}

const getCancelFlag = (value) => {
  if (pageType.value == 'cancelPage') {
    return 1
  } else if (pageType.value == 'oleSponsoredPage') {
    return 2
  } else if (pageType.value == 'visasRelativesFriendsPage') {
    return 5
  }

  return value.isActiveLeavePermitFlag ? 4 : 0
}

const setPageType = () => {
  const pageList = [
    {
      path: 'main',
      name: 'mainPage'
    },
    {
      path: 'canceled-files',
      name: 'cancelPage'
    },
    {
      path: 'old-sponsored',
      name: 'oleSponsoredPage'
    },
    {
      path: 'escape-available',
      name: 'escapeAvailablePage'
    },
    {
      path: 'active-leave-permits',
      name: 'activeLeavePermitsPage'
    },
    {
      path: 'visas-relatives-friends',
      name: 'visasRelativesFriendsPage'
    },
    {
      path: 'in-out',
      name: 'inOutPage'
    }
  ]
  const match = pageList.find((page) => {
    return route.fullPath.indexOf(page.path) >= 0
  })
  pageType.value = match.name
}

const getIsVioLatorsFlag = (isViolatorsFlag) => {
  const transactionId = route.params.serviceId
  if (['mainPage', 'escapeAvailablePage'].includes(pageType.value)) {
    return { isViolatorsFlag }
  }

  if (['cancelPage', 'visasRelativesFriendsPage'].includes(pageType.value)) {
    return { isViolatorsFlag: transactionId == lookups.ServiceTransactions.fines }
  }
  return {}
}

const getCancelModule = () => {
  if (pageType.value == 'visasRelativesFriendsPage') {
    return 2
  }

  if (['cancelPage', 'oleSponsoredPage'].includes(pageType.value)) {
    const cancelModuleId = route.query.moduleId
    const transactionId = route.params.serviceId

    return cancelModuleId && transactionId == lookups.residenceServiceTransactions.newRequest
      ? lookups.module.visa
      : cancelModuleId
  }
}
const getOverridePayload = (payload) => {
  if (['mainPage', 'escapeAvailablePage', 'visasRelativesFriendsPage'].includes(pageType.value)) {
    return {
      ...payload,
      ...{
        employeeName: '',
        expiryFrom: '',
        expiryTo: '',
        nationalityId: ''
      }
    }
  }

  if (pageType.value == 'cancelPage') {
    return {
      ...payload,
      ...{
        employeeName: '',
        moduleId: '',
        serviceTransactionId: '',
        expiryFrom: '',
        expiryTo: '',
        nationalityId: ''
      }
    }
  }

  if (pageType.value == 'oleSponsoredPage') {
    return {
      ...payload,
      ...{
        employeeName: '',
        moduleId: '',
        serviceTransactionId: '',
        expiryFrom: '',
        expiryTo: '',
        nationalityId: ''
      }
    }
  }
}
const mainPagePayload = () => {
  if (['mainPage', 'escapeAvailablePage', 'visasRelativesFriendsPage'].includes(pageType.value)) {
    return {
      isAbscondingPersonFlag: route.query.escapedPersons == 1 ? 1 : 0,
      getGoldenSponsoredFlag: client_config.enableNewGoldenSponsoredPage ? 2 : 0
    }
  }
}
const callServer = async (value) => {
  const cancelFlag = getCancelFlag(value)
  let payload = {
    ...value,
    serviceTransactionId: route.params.serviceId,
    adminRegionId: route.query.adminRegionId,
    canceledSponsoredFlag: cancelFlag,
    cancelDate: null,
    ...mainPagePayload(),
    loadLookupDataFromCache: false,
    ...getIsVioLatorsFlag(value.isViolatorsFlag),
    moduleId: getCancelModule()
  }
  if (payload.unifiedNumber) {
    payload = getOverridePayload(payload)
  } else {
    payload = {
      ...payload,
      ...{
        unifiedNumber: ''
      }
    }
  }
  try {
    const response = await apiHelperService.post('/api/individual/sponsored', payload)
    if (response) {
      return response
    }
  } catch (error) {
    console.error('callServer', error)
  }
}

const loadModules = async () => {
  const result = await lookupsStore.fetchLookup('/api/lookup/modules')
  extendModules.value = result
  modules.value = result.filter(
    (f) => f.id === lookups.module.residency || f.id === lookups.module.visa
  )
}

const loadServices = async () => {
  service.value = await lookupsStore.fetchLookup('/api/lookup/services')
}

const loadModuleParentService = async () => {
  parentService.value = await lookupsStore.fetchLookup('/api/lookup/moduleParentService')
}

const loadTransactions = async () => {
  transaction.value = await lookupsStore.fetchLookup('/api/lookup/transactions')
}

const loadCountries = async () => {
  countries.value = await lookupsStore.fetchLookup('/api/lookup/countries')
}
const loadFileStatus = async () => {
  serviceStatus.value = await lookupsStore.fetchLookup('/api/lookup/fileStatus')
}

const loadSortableColumns = async () => {
  specSponsoredOrderBy.value = await lookupsStore.fetchLookup('/api/lookup/sortableColumns')
  specSponsoredOrderBy.value = specSponsoredOrderBy.value.map((s) => {
    s.text = currentLanguage.value === 'ar' ? s.arName : s.enName
    return s
  })
}

const setSearchTitle = () => {
  const nameList = {
    mainPage: i18n.t('sponsoredManagement'),
    cancelPage: i18n.t('canceledFilesmanagment'),
    oleSponsoredPage: i18n.t('oldemployeemanagment'),
    escapeAvailablePage: i18n.t('escapeAvailable'),
    activeLeavePermitsPage: i18n.t('activeLeavePermits'),
    visasRelativesFriendsPage: i18n.t('visasForVisitingRelativesAndFriends')
  }
  searchTitle.value = nameList[pageType.value]
}

const resetAll = async () => {
  setPageType()
  setSearchTitle()
  formValues.value.resetForm()
  initialValues.value = {
    currentPage: 1,
    pageSize: 10
  }
  tableData.value = await callServer(initialValues.value)

  await loadModules()
  await loadServices()
  await loadModuleParentService()
  await loadTransactions()
  await loadCountries()
  await loadFileStatus()
  await loadSortableColumns()
}
// Same component different route will not re-render, so use watch to reset the form
watch(
  () => route.fullPath,
  (newV, oldV) => {
    if (newV != oldV) {
      resetAll()
    }
  }
)

onMounted(async () => {
  resetAll()
})

onBeforeUnmount(() => {
  formStore.$reset()
})
</script>

<style></style>
